/**
 * The active chat provider and the web search toggle, for every surface that
 * shows a "Web search" pill: the chat composer, the launcher composer and the
 * net-impact drill-in Chat tab.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { type ChatProvider, DEFAULT_CHAT_PROVIDER, getCapabilities } from '@upright/common/chat/provider-capabilities';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { useIsTeam } from '../common/generic/hooks';
import type { WebSearchHandOff } from './chat-panel-state';
import {
  getSelectedChatProvider,
  getWebSearchChoice,
  publishActiveChatProvider,
  setWebSearchChoice,
  subscribeSelectedChatProvider,
  subscribeWebSearchChoice,
  webSearchOffAtChatStart,
} from './upright-chat-shared';

/**
 * Tooltip for the pill in EU chats. The user turns web search on per chat, so
 * the tooltip is where they learn what that sends and where (PLT-7271).
 */
const EU_WEB_SEARCH_TOOLTIP =
  'Web access. Off at the start of each chat. When on, search terms written from this conversation are sent to a search service outside the EU.';

/**
 * The provider this user's chat runs on, mirroring `resolveChatProvider` on
 * the backend (PLT-7018), or `null` while a customer's EU residency privilege
 * is still loading:
 *
 *   - A non-team user runs on `'bedrock-eu'` exactly when their user group has
 *     been granted `USE_AI_CHAT_EU_RESIDENCY`, and on the default otherwise.
 *     They have no switch of their own.
 *   - A team user runs on whatever the staff EU testing switch in the chat
 *     debug panel says. The team shortcut makes every team user pass the
 *     privilege check, so for them the grant cannot be the signal.
 *
 * The switch lives in a pub-sub store so flipping it re-renders the composer
 * immediately. The resolved value is published for the adapter, which waits
 * for it before deciding on web search (`webSearchFieldsForNextRequest`).
 */
export function useActiveChatProvider(): ChatProvider | null {
  const isTeam = useIsTeam();
  const { hasPrivilege: hasEuResidency, loading } = useHasResourcelessPrivilege(
    ResourcelessPermissionId.USE_AI_CHAT_EU_RESIDENCY,
  );
  const switchProvider = useSyncExternalStore(
    subscribeSelectedChatProvider,
    getSelectedChatProvider,
    getSelectedChatProvider,
  );
  let provider: ChatProvider | null;
  if (isTeam) {
    provider = switchProvider;
  } else if (loading) {
    provider = null;
  } else {
    provider = hasEuResidency ? 'bedrock-eu' : DEFAULT_CHAT_PROVIDER;
  }
  useEffect(() => {
    if (provider !== null) publishActiveChatProvider(provider);
  }, [provider]);
  return provider;
}

export interface WebSearchToggle {
  /** The active provider, or `null` while it is loading. */
  provider: ChatProvider | null;
  /** The active provider has web search, and the provider is known. */
  available: boolean;
  /** Web search is on for the active provider. */
  enabled: boolean;
  /** The choice goes back to off whenever a chat starts (EU chats). */
  offAtChatStart: boolean;
  tooltip: string;
  toggle: () => void;
}

export function useWebSearchToggle(): WebSearchToggle {
  const provider = useActiveChatProvider();
  const choice = useSyncExternalStore(subscribeWebSearchChoice, getWebSearchChoice, getWebSearchChoice);
  if (provider === null) {
    return {
      provider,
      available: false,
      enabled: false,
      offAtChatStart: false,
      tooltip: 'Web access',
      toggle: () => {},
    };
  }
  const available = getCapabilities(provider).webSearch !== false;
  const enabled = available && choice[provider];
  const offAtChatStart = webSearchOffAtChatStart(provider);
  return {
    provider,
    available,
    enabled,
    offAtChatStart,
    tooltip: offAtChatStart ? EU_WEB_SEARCH_TOOLTIP : 'Web access',
    toggle: () => {
      setWebSearchChoice(provider, !enabled);
    },
  };
}

/**
 * The pill on a launcher, which hands off to a chat: the launcher composer and
 * the net-impact drill-in Chat tab.
 *
 * On a provider whose web search is off at the start of each chat (EU), the
 * launcher keeps its own choice, which starts off whenever the launcher
 * mounts, and `handOff` carries it into the chat it opens. The hand-off
 * applies it after the new chat's reset, so a choice from an earlier chat
 * never reaches the new one and the launcher's choice is not lost.
 *
 * On other providers this is `useWebSearchToggle` and `handOff` is `null`: the
 * shared choice already survives the hand-off.
 */
export function useLauncherWebSearchToggle(): WebSearchToggle & { handOff: WebSearchHandOff | null } {
  const shared = useWebSearchToggle();
  const [launcherChoice, setLauncherChoice] = useState(false);
  const { provider } = shared;
  if (provider === null || !shared.offAtChatStart) return { ...shared, handOff: null };
  const enabled = shared.available && launcherChoice;
  return {
    ...shared,
    enabled,
    toggle: () => {
      setLauncherChoice(!enabled);
    },
    handOff: { provider, enabled },
  };
}
